import React, { useState } from 'react';

// Simple Tooltip Component
const Tooltip = ({ 
  children, 
  content, 
  position = 'top',
  delay = 200,
  maxWidth = '250px',
  minWidth = null,
  width = null,
  backgroundColor = '#1f2937',
  textColor = 'white',
  fontSize = '14px',
  padding = '8px 12px',
  borderRadius = '6px',
  fontWeight = '500',
  lineHeight = '1.4',
  whiteSpace = 'normal',
  wordWrap = 'break-word',
  textAlign = 'left'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);

  const showTooltip = () => {
    const id = setTimeout(() => setIsVisible(true), delay);
    setTimeoutId(id);
  };

  const hideTooltip = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setIsVisible(false);
  };

  const getPositionStyles = () => {
    const baseStyles = {
      position: 'absolute',
      zIndex: 1000,
      padding,
      backgroundColor,
      color: textColor,
      borderRadius,
      fontSize,
      fontWeight,
      lineHeight,
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      whiteSpace,
      wordWrap,
      textAlign
    };

    // Add width constraints
    if (width) {
      baseStyles.width = width;
    } else {
      if (maxWidth) baseStyles.maxWidth = maxWidth;
      if (minWidth) baseStyles.minWidth = minWidth;
    }

    switch (position) {
      case 'top':
        return {
          ...baseStyles,
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: '8px'
        };
      case 'bottom':
        return {
          ...baseStyles,
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: '8px'
        };
      case 'left':
        return {
          ...baseStyles,
          right: '100%',
          top: '50%',
          transform: 'translateY(-50%)',
          marginRight: '8px'
        };
      case 'right':
        return {
          ...baseStyles,
          left: '100%',
          top: '50%',
          transform: 'translateY(-50%)',
          marginLeft: '8px'
        };
      default:
        return baseStyles;
    }
  };

  const getArrowStyles = () => {
    const arrowSize = '5px';
    const baseArrow = {
      position: 'absolute',
      width: 0,
      height: 0,
      borderStyle: 'solid'
    };

    switch (position) {
      case 'top':
        return {
          ...baseArrow,
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          borderWidth: `${arrowSize} ${arrowSize} 0 ${arrowSize}`,
          borderColor: `${backgroundColor} transparent transparent transparent`
        };
      case 'bottom':
        return {
          ...baseArrow,
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          borderWidth: `0 ${arrowSize} ${arrowSize} ${arrowSize}`,
          borderColor: `transparent transparent ${backgroundColor} transparent`
        };
      case 'left':
        return {
          ...baseArrow,
          left: '100%',
          top: '50%',
          transform: 'translateY(-50%)',
          borderWidth: `${arrowSize} 0 ${arrowSize} ${arrowSize}`,
          borderColor: `transparent transparent transparent ${backgroundColor}`
        };
      case 'right':
        return {
          ...baseArrow,
          right: '100%',
          top: '50%',
          transform: 'translateY(-50%)',
          borderWidth: `${arrowSize} ${arrowSize} ${arrowSize} 0`,
          borderColor: `transparent ${backgroundColor} transparent transparent`
        };
      default:
        return {};
    }
  };

  return (
    <div 
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      {children}
      {isVisible && (
        <div style={getPositionStyles()}>
          {content}
          <div style={getArrowStyles()}></div>
        </div>
      )}
    </div>
  );
};

// Info Icon Component
const InfoIcon = ({ 
  tooltip, 
  size = '16px', 
  color = '#64748b',
  position = 'top',
  // Pass through all tooltip customization props
  maxWidth,
  minWidth,
  width,
  backgroundColor,
  textColor,
  fontSize,
  padding,
  borderRadius,
  fontWeight,
  lineHeight,
  whiteSpace,
  wordWrap,
  textAlign
}) => (
  <Tooltip 
    content={tooltip} 
    position={position}
    maxWidth={maxWidth}
    minWidth={minWidth}
    width={width}
    backgroundColor={backgroundColor}
    textColor={textColor}
    fontSize={fontSize}
    padding={padding}
    borderRadius={borderRadius}
    fontWeight={fontWeight}
    lineHeight={lineHeight}
    whiteSpace={whiteSpace}
    wordWrap={wordWrap}
    textAlign={textAlign}
  >
    <svg 
      style={{ 
        width: size, 
        height: size, 
        color: color,
        cursor: 'help',
        marginLeft: '6px',
        marginBottom: '6px',
        verticalAlign: 'middle'
      }} 
      fill="currentColor" 
      viewBox="0 0 20 20"
    >
      <path 
        fillRule="evenodd" 
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" 
        clipRule="evenodd" 
      />
    </svg>
  </Tooltip>
);

export { Tooltip, InfoIcon };